// Attachments load on demand, as tables do and for the same reason: most editors never hold one,
// and a Lexical editor cannot be given node types after it is built, so the load always finishes
// before the editor exists. An editor needs them when its host gives it somewhere to keep files,
// when its document already holds one, or when it is shared.
// Type only, so it is erased: the one real import of this module is the dynamic one below.
import type * as Attachments from "../features/attachments/index";
import {
  createModuleLoader,
  type ModuleLoader,
  type ModuleStatus,
  type ModuleSupport,
} from "./module-loader";

export type AttachmentsModule = typeof Attachments;

const loader = createModuleLoader<AttachmentsModule>(() => import("../features/attachments/index"));

/** Starts the load if nothing has, and again after a failure. Safe to call on every render. */
export const loadAttachments = (): void => loader.load();

/** Where the load of attachment support stands, for every editor on the page. */
export type AttachmentSupportPhase = ModuleStatus<AttachmentsModule>["phase"];

/** Read-only, and shaped for `useSyncExternalStore`. Asking never starts the load. */
export const attachmentSupport: ModuleSupport<AttachmentsModule> = {
  subscribe: loader.subscribe,
  getPhase: () => loader.get().phase,
};

export const attachmentsStatus: Pick<ModuleLoader<AttachmentsModule>, "subscribe" | "get"> = {
  subscribe: loader.subscribe,
  get: loader.get,
};

/**
 * Fetches attachment support ahead of time, for a host that knows a document with attachments is
 * about to open. Resolves when editors can use it.
 */
export const preloadAttachments = (): Promise<void> => loader.preload();
